import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import './ViewTransition.css';

type Props = {
  viewKey: string;
  dir: 'push' | 'pop';
  morph?: boolean;
  wrapperRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
};

export function ViewTransition({ viewKey, dir, morph = false, wrapperRef, children }: Props) {
  const [phase, setPhase] = useState<'enter' | 'idle'>('idle');
  const prevKey = useRef(viewKey);
  const prevDir = useRef(dir);

  useLayoutEffect(() => {
    if (viewKey !== prevKey.current) {
      prevKey.current = viewKey;
      prevDir.current = dir;
      setPhase('enter');

      // Tag whichever element should morph for THIS transition:
      // - push+morph: this wrapper is the incoming ("new") full-screen view
      //   — Tile.tsx already tagged the origin tile as "old" before calling
      //   nav.push, synchronously, so the browser captures both ends.
      // - pop+morph: this wrapper was already tagged as "old" (by App.tsx's
      //   popWithMorph, before nav.pop ran) — it must NOT also claim to be
      //   "new" here, or the duplicate name makes the browser skip the
      //   transition entirely. HomeView tags the destination tile as "new"
      //   instead (same synchronous flushSync window), so this wrapper just
      //   clears itself.
      // - anything else: no morph, always clear.
      if (wrapperRef.current) {
        wrapperRef.current.style.viewTransitionName = morph && dir === 'push' ? 'tile-morph' : '';
      }

      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPhase('idle');
          if (wrapperRef.current) {
            wrapperRef.current.style.viewTransitionName = '';
          }
        });
      });
      return () => cancelAnimationFrame(id);
    }
  }, [viewKey, dir, morph, wrapperRef]);

  const hasNativeVT = typeof document !== 'undefined' && 'startViewTransition' in document;
  const cls = (!hasNativeVT && phase === 'enter')
    ? `vt-enter vt-enter-${prevDir.current === 'push' ? 'right' : 'left'}`
    : '';

  return (
    <div ref={wrapperRef} className={`view-transition-wrapper ${cls}`.trim()}>
      {children}
    </div>
  );
}
